import {
  PathPickMarketOption,
  PathPickPoizonOption,
  PathPickProduct,
  PathPickProductRequestSlot,
  PopularProductSeed,
} from "@/features/pathpick/types";
import {
  PathPickKreamOptionsResponse,
  PathPickPoizonOptionsResponse,
} from "@/features/pathpick/types/response";
import { createPopularProduct } from "@/features/pathpick/utils";
import { START_GUIDE_IMAGES } from "@/features/pathpick/features/start-guide/constants";
// 훅이 이 파일의 DEMO_PRODUCT_IDS 를 쓰므로 여기서는 타입만 가져온다 — 런타임 순환 참조를 만들지 않는다.
import type { DemoProductState } from "@/features/pathpick/features/start-guide/hooks/useStartGuideDemoState";

// 가이드 전용 목 상품 id — 데모가 쓰는 실제 카탈로그 id를 그대로 쓴다.
// createPopularProduct 가 kream.productId·poizon.spuId 도 이 값으로 채우므로
// 두 마켓의 옵션 캐시 키(["pathpick","kream-options"|"poizon-options", id])도 같은 값이 된다.
export const DEMO_NORMAL_PRODUCT_ID = 1552610;
export const DEMO_HOT_DEAL_PRODUCT_ID = 1551256;

// 핫딜 마감일 — 고정 문자열로 둔다. 모듈 로드 시각에 따라 D-day 배지가 흔들리면 안 된다.
const DEMO_HOT_DEAL_DEADLINE = "2026-09-30";

// 시드는 lowestValue/lastValue 에 같은 숫자를 넣기 때문에, 비교 기준 토글이 의미를 가지려면
// 두 값을 따로 지정해야 한다. 상품 레벨 값과 옵션 레벨 값도 실제 응답처럼 조금 다르게 둔다
// (상품 레벨은 목록 API, 옵션 레벨은 상세 진입 시점 호출에서 오는 별개의 수치다).
interface DemoMarketPrice {
  lowestValue: number;
  lastValue: number;
}

interface DemoMarketOption extends DemoMarketPrice {
  // 패스레이드 옵션명과 다르면 상세 모달의 "마켓 전용 옵션" 그룹으로 빠진다.
  name: string;
}

const NORMAL_SEED: PopularProductSeed = {
  id: DEMO_NORMAL_PRODUCT_ID,
  brandName: "Celine",
  name: "[Celine] Other Accessory FW26 10L033DPV",
  sku: "10L033DPV",
  categories: ["잡화", "Other Accessory"],
  season: "FW26",
  color: "Black",
  price: 2_623_000,
  kreamPrice: 3_384_000,
  kreamLastPrice: 3_486_000,
  kreamBidCount: 177,
  poizonPrice: 3_232_000,
  poizonLastPrice: 3_174_000,
  poizonSales30d: 9,
  expectedMargin: 761_000,
  // 마진율은 공급가가 아니라 기준가로 나눈다 — 761,000 / 3,384,000
  marginRate: 22.5,
  sizes: [{ name: "ONE SIZE", stock: 1 }],
};

// 포이즌에만 있는 사이즈 — 상세 모달의 `POIZON 옵션` 그룹과 미매칭 배너를 보여주기 위한 값이다.
const NORMAL_KREAM_OPTIONS: DemoMarketOption[] = [
  { name: "ONE SIZE", lowestValue: 3_384_000, lastValue: 3_486_000 },
];
const NORMAL_POIZON_OPTIONS: DemoMarketOption[] = [
  { name: "ONE SIZE POIZON", lowestValue: 3_222_000, lastValue: 3_164_000 },
];

const HOT_DEAL_SEED: PopularProductSeed = {
  id: DEMO_HOT_DEAL_PRODUCT_ID,
  brandName: "Gucci",
  name: "[Gucci] Cross body bag FW26 875806FAFV4",
  sku: "875806FAFV4",
  categories: ["잡화", "Cross body bag"],
  season: "FW26",
  color: "Beige",
  // 전달서 §9 고정값 — 공급가 650,000 / 예상 마진 387,000
  price: 650_000,
  kreamPrice: 1_037_000,
  kreamLastPrice: 1_063_000,
  kreamBidCount: 112,
  poizonPrice: 990_000,
  poizonLastPrice: 960_000,
  poizonSales30d: 23,
  expectedMargin: 387_000,
  // 387,000 / 1,037,000
  marginRate: 37.3,
  sizes: [{ name: "ONE SIZE", stock: 2 }],
};

// 양쪽 마켓에 같은 사이즈가 있어 `매칭` 상태와 추천 문구를 보여주는 상품이다.
const HOT_DEAL_KREAM_OPTIONS: DemoMarketOption[] = [
  { name: "ONE SIZE", lowestValue: 1_037_000, lastValue: 1_063_000 },
];
const HOT_DEAL_POIZON_OPTIONS: DemoMarketOption[] = [
  { name: "ONE SIZE", lowestValue: 980_000, lastValue: 950_000 },
];

// 시드 → 카드가 그대로 받는 PathPickProduct.
// 카드는 item.expectedMargin 을 쓰지 않고 (KREAM가 − 공급가)로 직접 계산하므로,
// 기준을 바꾸면 마진이 API 없이 즉시 다시 계산된다.
function createDemoProduct(
  seed: PopularProductSeed,
  isHotDeal: boolean,
  imageUrl: string
): PathPickProduct {
  const base = createPopularProduct(seed);
  return {
    ...base,
    // 핫딜 판정은 productType 하나로 한다 — 카드가 이 값으로 핫딜 배지를 단다.
    productType: isHotDeal ? "LINE_SHEET_PRODUCT" : "PRODUCT",
    // 핫딜에만 마감일이 있고, 카드가 이 값으로 D-day 배지를 만든다.
    deadline: isHotDeal ? DEMO_HOT_DEAL_DEADLINE : null,
    gender: "여성",
    imageUrls: [imageUrl],
  };
}

export const DEMO_NORMAL_PRODUCT = createDemoProduct(
  NORMAL_SEED,
  false,
  START_GUIDE_IMAGES.celine
);
export const DEMO_HOT_DEAL_PRODUCT = createDemoProduct(
  HOT_DEAL_SEED,
  true,
  START_GUIDE_IMAGES.gucci
);

// 일반 → 핫딜 순서. 가이드의 모든 화면이 같은 두 상품을 같은 순서로 쓴다.
export const DEMO_PRODUCTS: PathPickProduct[] = [
  DEMO_NORMAL_PRODUCT,
  DEMO_HOT_DEAL_PRODUCT,
];

export const DEMO_PRODUCT_IDS = DEMO_PRODUCTS.map((item) => item.id);

// 주문 체험은 두 상품을 지정 수량만큼 정확히 입력해야 통과한다 —
// 수량 모달의 `주문 요청` 활성 조건이자 입력 아래 안내 문구의 기준값이다.
// 값 자체는 각 상품의 재고와 같지만 의미가 다르므로 시드에서 파생시키지 않고 명시한다.
export const DEMO_REQUIRED_ORDER_QUANTITY: Record<number, number> = {
  [DEMO_NORMAL_PRODUCT_ID]: 1, // 10L033DPV
  [DEMO_HOT_DEAL_PRODUCT_ID]: 2, // 875806FAFV4
};

// 목 처리를 마친 채널 슬롯. 가이드는 요청 id 로 열 상세가 없어 신청일·id 는 비운다.
const COMPLETED_DEMO_SLOT: PathPickProductRequestSlot = {
  isCompleted: true,
  status: "SUCCESS",
  requestedAt: null,
  requestId: null,
};

/**
 * 목 진행 상태를 실제 카드가 읽는 `requests` 모양으로 바꿔 준다.
 *
 * 카드의 상태 배지(`파일 생성 완료 | N` / `API 연동 완료`)는 `item.requests` 하나만 보고 그려진다
 * (`getPathPickProductBadgeState`). 카드에 배지 prop 을 새로 뚫는 대신 여기서 데이터를 맞춰 주면
 * 가이드도 운영 화면과 똑같은 배지를 얻는다.
 *
 * 진행이 없는 상태(`saved`/`unlinked`/`deleted`/미지정)는 원본을 그대로 돌려준다.
 */
export const withDemoRequestState = (
  item: PathPickProduct,
  state: DemoProductState | undefined
): PathPickProduct => {
  if (state !== "file-ready" && state !== "linked") {
    return item;
  }

  return {
    ...item,
    requests: {
      naverSync: state === "linked" ? COMPLETED_DEMO_SLOT : null,
      naverExcel: state === "file-ready" ? COMPLETED_DEMO_SLOT : null,
      kreamExcel: null,
      kreamExcelConfirmedQuantity: null,
    },
  };
};

// 상품 상세 정보 모달이 여는 마켓 옵션 조회의 목 응답.
// 모달을 열기 전에 이걸 react-query 캐시에 심어 두면 모달을 고치지 않고도 목으로 돈다.
const toDemoMarketOption = (option: DemoMarketOption): PathPickMarketOption => ({
  name: option.name,
  displayName: option.name,
  price: { lowestValue: option.lowestValue, lastValue: option.lastValue },
  lastSaleDt: null,
  deliveryType: null,
  isOnlyBid: false,
});

function createDemoKreamOptions(
  productId: number,
  options: DemoMarketOption[]
): PathPickKreamOptionsResponse {
  return {
    market: "kream",
    productId,
    kreamProductId: productId,
    source: "live",
    options: options.map(toDemoMarketOption),
  };
}

function createDemoPoizonOptions(
  spuId: number,
  options: DemoMarketOption[],
  sales30d: number
): PathPickPoizonOptionsResponse {
  // 데모는 setQueryData로 캐시에 직접 들어가 camel 변환기를 거치지 않으므로,
  // 실제 응답과 같은 키(sales_30d)로 맞춰야 옵션 표가 데모에서만 다르게 그려지지 않는다.
  const poizonOptions: PathPickPoizonOption[] = options.map((option) => ({
    ...toDemoMarketOption(option),
    sales_30d: sales30d,
  }));
  return {
    market: "poizon",
    productId: spuId,
    source: "live",
    options: poizonOptions,
  };
}

export const DEMO_KREAM_OPTIONS: Record<number, PathPickKreamOptionsResponse> =
  {
    [DEMO_NORMAL_PRODUCT_ID]: createDemoKreamOptions(
      DEMO_NORMAL_PRODUCT_ID,
      NORMAL_KREAM_OPTIONS
    ),
    [DEMO_HOT_DEAL_PRODUCT_ID]: createDemoKreamOptions(
      DEMO_HOT_DEAL_PRODUCT_ID,
      HOT_DEAL_KREAM_OPTIONS
    ),
  };

export const DEMO_POIZON_OPTIONS: Record<
  number,
  PathPickPoizonOptionsResponse
> = {
  [DEMO_NORMAL_PRODUCT_ID]: createDemoPoizonOptions(
    DEMO_NORMAL_PRODUCT_ID,
    NORMAL_POIZON_OPTIONS,
    NORMAL_SEED.poizonSales30d ?? 0
  ),
  [DEMO_HOT_DEAL_PRODUCT_ID]: createDemoPoizonOptions(
    DEMO_HOT_DEAL_PRODUCT_ID,
    HOT_DEAL_POIZON_OPTIONS,
    HOT_DEAL_SEED.poizonSales30d ?? 0
  ),
};
